import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SectionLines from "../common/SectionLines";
import useWakeLock from "../../hooks/useWakeLock";
import { sendReaction } from "../../actions/sessionActions";
import { selectCurrentSong, selectCurrentVerse, selectHasSong, selectTranspose } from "../../selectors";
import { sortedVerses, verseLabel } from "../../lib/lyrics";
import { isLive, statusWord } from "./liveStatus";
import AaPopover from "./AaPopover";
import RequestSheet from "./RequestSheet";

const FLOAT_MS = 1600;
const SCROLL_MARGIN = 14; // px kept above the live section when the page brings it into view
const SETTLE_MS = 1000; // the longest a scroll the page started is ignored by the follow logic

const liveElement = (container) => container.querySelector('[data-live="1"]');

/**
 * What a guest sees: the whole song, the section on screens coloured and in the chosen
 * size, the rest quieter. The page follows the host; a guest who scrolls away gets a
 * Follow button back to the live section. A heart for the host, and + to request a song.
 */
const GuestLivePage = () => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const song = useSelector(selectCurrentSong);
  const hasSong = useSelector(selectHasSong);
  const liveVerse = useSelector(selectCurrentVerse);
  const showChords = useSelector((state) => state.preferences.showChords);
  const keepAwake = useSelector((state) => state.preferences.keepAwake);
  const transpose = useSelector((state) => selectTranspose(state, song.Id));
  const [floaters, setFloaters] = useState([]);
  const [following, setFollowing] = useState(true);
  const [aaOpen, setAaOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const scrollRef = useRef(null);
  const followingRef = useRef(true);
  const autoScroll = useRef(null); // { target, until } while the page scrolls itself
  const shown = useRef({ songId: null, order: null }); // what was last brought into view
  useWakeLock(keepAwake);

  const verses = sortedVerses(song);
  const liveIndex = Math.max(0, verses.indexOf(liveVerse));
  const liveOrder = liveVerse?.Order;

  useEffect(() => {
    followingRef.current = following;
  }, [following]);

  const scrollToLive = useCallback((smooth) => {
    const container = scrollRef.current;
    const target = container && liveElement(container);
    if (!target) return;
    const top = Math.max(0, target.offsetTop - SCROLL_MARGIN);
    autoScroll.current = { target: top, until: Date.now() + SETTLE_MS };
    if (smooth && typeof container.scrollTo === "function") container.scrollTo({ top, behavior: "smooth" });
    else container.scrollTop = top;
  }, []);

  // The page follows the host: a new section glides into view, a new song starts at its first one.
  useEffect(() => {
    const previous = shown.current;
    if (previous.songId === song.Id && previous.order === liveOrder) return undefined;
    shown.current = { songId: song.Id, order: liveOrder };
    const sameSong = previous.songId === song.Id;
    if (sameSong && !followingRef.current) return undefined; // the guest wandered off; Follow brings them back
    if (!sameSong) setFollowing(true);
    scrollToLive(sameSong);
    if (sameSong) return undefined;
    const frame = requestAnimationFrame(() => scrollToLive(false)); // once the fonts have settled
    return () => cancelAnimationFrame(frame);
  }, [song.Id, liveOrder, scrollToLive]);

  // Whether the live section is still in view decides between following and the Follow button.
  const onScroll = () => {
    const container = scrollRef.current;
    if (!container || !container.clientHeight) return;
    const auto = autoScroll.current;
    if (auto) {
      if (Math.abs(container.scrollTop - auto.target) < 2 || Date.now() > auto.until) autoScroll.current = null;
      return;
    }
    const target = liveElement(container);
    if (!target) return;
    const top = target.offsetTop - container.scrollTop;
    setFollowing(top > -target.offsetHeight * 0.5 && top < container.clientHeight * 0.6);
  };

  // a finger or a wheel ends any scroll the page started
  const userTakesOver = () => {
    autoScroll.current = null;
  };

  const follow = () => {
    setFollowing(true);
    scrollToLive(true);
  };

  const react = () => {
    dispatch(sendReaction(session.Code));
    const id = Date.now() + Math.random();
    setFloaters((current) => [...current, id]);
    setTimeout(() => setFloaters((current) => current.filter((floater) => floater !== id)), FLOAT_MS);
  };

  return (
    <div className="live" data-screen-label="Live · guest">
      <div className="live-head">
        <div className="live-head-text">
          <span className="live-title">{song.Title}</span>
          <span className="live-status" aria-live="polite">
            <span className={`dot${isLive(session) ? "" : " dot-off"}`} aria-hidden="true" />
            <span>
              {statusWord(session)} · {session.Code}
            </span>
          </span>
        </div>
        <div className="live-head-tools">
          <button type="button" className="btn btn-round btn-sm btn-outline" aria-pressed={aaOpen} aria-expanded={aaOpen} aria-label="Display settings" onClick={() => setAaOpen((open) => !open)}>
            Aa
          </button>
          {aaOpen && <AaPopover song={song} onClose={() => setAaOpen(false)} />}
        </div>
      </div>

      <div className="live-stage">
        <div ref={scrollRef} className="live-scroll" onScroll={onScroll} onPointerDown={userTakesOver} onWheel={userTakesOver}>
          {hasSong ? (
            verses.map((verse, index) => {
              const on = index === liveIndex;
              const place = on ? "on" : index < liveIndex ? "past" : "ahead";
              return (
                <div key={verse.Order} className={`live-sec ${place}`} data-live={on ? "1" : "0"} aria-current={on ? "true" : undefined}>
                  <span className={`chip${on ? "" : " chip-outline"}`}>{verseLabel(verse, index)}</span>
                  <SectionLines verse={verse} showChords={showChords} transpose={transpose} size={on ? "lg" : "md"} />
                </div>
              );
            })
          ) : (
            <div className="live-sec on" data-live="1">
              <span className="chip">Waiting for the host</span>
              <SectionLines verse={liveVerse} size="lg" />
            </div>
          )}
        </div>

        {!following && (
          <button type="button" className="follow-btn" onClick={follow}>
            <span aria-hidden="true">⌖</span>
            <span>Follow</span>
          </button>
        )}

        <div className="live-fab">
          {floaters.map((id) => (
            <span key={id} className="floater" aria-hidden="true">
              ♡
            </span>
          ))}
          <button type="button" className="btn btn-round btn-soft-accent" onClick={react} aria-label="Send a heart">
            ♡
          </button>
          <button type="button" className="btn btn-round btn-outline" onClick={() => setRequesting(true)} aria-label="Request a song">
            +
          </button>
        </div>
      </div>

      {requesting && <RequestSheet onClose={() => setRequesting(false)} />}
    </div>
  );
};

export default GuestLivePage;
